import { createContext, PropsWithChildren, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { useGetExternalSupplierProfileQuery } from "@/redux/apis/externalSupplier/externalSupplierApi";
import { ExternalSupplierProfile } from "@/redux/slices/externalSupplier/types";

type Modal = "save-changes";
type Section = "details";

interface IContext {
  externalSupplier: ExternalSupplierProfile;
  isFetching: boolean;
  activeModal?: Modal;
  setActiveModal: (modal?: Modal) => void;
  activeSection?: Section;
  setActiveSection: (section?: Section) => void;
}

export const ExternalSupplierProfileProfileContext = createContext<IContext | null>(null);

export const ExternalSupplierProfileProfileProvider = ({ children }: PropsWithChildren) => {
  const { externalSupplierId } = useParams<{ externalSupplierId: string }>();
  const { data: externalSupplier, isFetching } = useGetExternalSupplierProfileQuery(externalSupplierId ?? "", {
    skip: !externalSupplierId,
  });
  const [activeModal, setActiveModal] = useState<IContext["activeModal"]>(undefined);
  const [activeSection, setActiveSection] = useState<IContext["activeSection"]>(undefined);

  if (isFetching || !externalSupplier) return <Loading className="!bg-transparent" />;

  return (
    <ExternalSupplierProfileProfileContext.Provider
      value={{ externalSupplier: externalSupplier, isFetching, activeModal, setActiveModal, activeSection, setActiveSection }}
    >
      {children}
    </ExternalSupplierProfileProfileContext.Provider>
  );
};

export const useExternalSupplierProfileProfileContext = () => {
  const context = useContext(ExternalSupplierProfileProfileContext);
  if (!context) {
    throw new Error("useExternalSupplierProfileProfileContext must be used within an ExternalSupplierProfileProfileProvider");
  }
  return context;
};
